import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, Lock, Minus, Plus } from "lucide-react";
import { unwrap } from "@majourneys/api-client";
import {
  activityKeys,
  checkoutErrorText,
  createCheckoutApi,
  PhoneField,
  useBookingContext,
  useBookingTrack,
} from "@majourneys/booking";
import { useDisplayMoney } from "@majourneys/currency";
import { ageBracketLabels, isRideTooSoon, normalizeMinPeople } from "@majourneys/shared";
import { CONTROL_EDGE, CONTROL_LIT } from "@majourneys/shared/ui";
import type { BookingProposal } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { markProposalOpened, type RequestSentInfo } from "../lib/proposal-status";
import {
  chatBookingKind,
  chatBookingTotal,
  chatCheckoutPayload,
  detailsComplete,
  guestsComplete,
  handOffReason,
  initialChatBooking,
  missingSteps,
  packLines,
  paymentLine,
  phoneOk,
  pickupFor,
  selectedPackage,
  typePriceOf,
  EMAIL_SHAPE,
  type ChatActivity,
  type ChatBookingState,
  type ChatBookingStep,
} from "../lib/chat-booking";
import { addLocalBooking } from "../../account/lib/local-bookings";
import { useBookingSessionUser } from "../../account/hooks/use-booking-session-user";
import { isFullName } from "@majourneys/shared";
import { SOFT } from "./soft-tokens";
import { panelStyle } from "../../../shared/ui/card-frame";

/**
 * Booking inside the chat (owner, 2026-10-12; lab: pewter-chat-kit-lab, C31 to
 * C40). Opens under the proposal when the guest presses "Check details and
 * send", and asks what the place's booking sheet asks, one card at a time:
 *
 *   package (C31) or treatments (C35) · who is coming + pickup (C32)
 *   · your details (C33) · review and send (C40)
 *
 * A "still needed" strip (C39) keeps the guest oriented. Every rule and the
 * checkout body come from lib/chat-booking.ts, which reads the sheet's own
 * shared rules; the server re-prices and re-checks. On success the proposal
 * turns into "Request sent" (C11) through the same proof the sheet leaves
 * (lib/proposal-status + the local bookings list).
 *
 * Places that book on their own form (nightclubs, transfers, paused venues)
 * never reach here: `onHandOff` sends the guest to that form instead.
 */

const CONTROL = { background: CONTROL_LIT, boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
const ON = { background: SOFT.buttonFace, color: "#fff" } as const;
const GOLD_EDGE = "inset 0 0 0 1.5px rgba(138,100,37,0.34)";
const HAIR = "rgba(17,14,57,0.08)";

export function ChatBookingCard({
  proposal,
  lang,
  messageId,
  onHandOff,
  onSent,
}: {
  proposal: BookingProposal;
  lang: ChatLang;
  messageId?: string;
  /** The place books on its own form: open it, pre-filled. */
  onHandOff: () => void;
  /** The checkout accepted it: what to show as "Request sent" right away. */
  onSent?: (info: RequestSentInfo) => void;
}) {
  const { t, i18n } = useTranslation();
  const { api } = useBookingContext();
  const query = useQuery({
    queryKey: activityKeys.detail(proposal.slug, i18n.language),
    queryFn: async () =>
      unwrap(await api.activities.explore({ slug: proposal.slug }).get({ query: { lang: i18n.language } })),
    staleTime: 5 * 60_000,
  });

  if (query.isPending) {
    return (
      <p className="mt-3 text-start text-[12.5px] font-medium" style={{ color: SOFT.body }} data-testid="chat-booking-loading">
        {t("ai_concierge.booking.loading")}
      </p>
    );
  }
  // Same cache entry as the booking sheet's `useActivity`: the body is `{ data }`.
  const activity = (query.data as unknown as { data?: ChatActivity } | undefined)?.data;
  if (query.isError || !activity || handOffReason(activity)) {
    return (
      <div className="mt-3" data-testid="chat-booking-handoff">
        <button
          type="button"
          onClick={onHandOff}
          className="mj-press inline-flex min-h-[40px] w-full items-center justify-center rounded-full px-4 text-[13px] font-semibold"
          style={{ ...ON, boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}` }}
        >
          {t("ai_concierge.booking.open_form")}
        </button>
      </div>
    );
  }
  return <Flow activity={activity} proposal={proposal} lang={lang} messageId={messageId} onSent={onSent} />;
}

function Flow({
  activity: a,
  proposal: p,
  lang,
  messageId,
  onSent,
}: {
  activity: ChatActivity;
  proposal: BookingProposal;
  lang: ChatLang;
  messageId?: string;
  onSent?: (info: RequestSentInfo) => void;
}) {
  const { t } = useTranslation();
  const { api } = useBookingContext();
  const track = useBookingTrack();
  const money = useDisplayMoney();
  const user = useBookingSessionUser();
  const [s, setS] = useState<ChatBookingState>(() => initialChatBooking(a, p, user));
  const set = (patch: Partial<ChatBookingState>) => setS((x) => ({ ...x, ...patch }));
  const kind = chatBookingKind(a);
  const tooSoon = isRideTooSoon({ date: p.date.slice(0, 10), time: p.time || null, serviceTimes: [] });
  const pick = pickupFor(a, s, tooSoon);
  const missing = missingSteps(a, s, tooSoon);
  const steps: ChatBookingStep[] = useMemo(() => {
    const list: ChatBookingStep[] = [];
    if ((kind === "single" && a.packages.length > 1) || kind === "multi") list.push("package");
    list.push("guests", "details", "review");
    return list;
  }, [kind, a.packages.length]);
  // The first step the guest has not confirmed. Moving on is the guest's tap,
  // not the state being complete: the AI may already know the party.
  // A package the AI already matched is shown as answered (with Edit), so the
  // guest starts on the first real question.
  const [at, setAt] = useState(() =>
    steps[0] === "package" && kind === "single" && initialChatBooking(a, p, user).packageId ? 1 : 0,
  );
  const step = steps[Math.min(at, steps.length - 1)]!;
  // Each new question is brought into view: it opens under the one just
  // answered, often below the fold of the thread.
  const current = useRef<HTMLElement | null>(null);
  const firstPaint = useRef(true);
  useEffect(() => {
    const el = current.current;
    if (!el || typeof el.scrollIntoView !== "function") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ block: firstPaint.current ? "nearest" : "start", behavior: reduce ? "auto" : "smooth" });
    firstPaint.current = false;
  }, [at]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const minParty = normalizeMinPeople(a.minPeople);

  const totalMad = chatBookingTotal(a, p, s, tooSoon);
  const shown = useMemo(() => {
    // Sum of rounded line prices, as the sheet shows it (owner, 2026-09-29).
    if (kind === "table") return 0;
    if (kind === "multi") return packLines(a, s).reduce((n, l) => n + money.unit(l.pkg.pricing.adult) * l.quantity, 0);
    return totalMad > 0 ? money.unit(totalMad) : 0;
  }, [kind, a, s, money, totalMad]);

  const canNext =
    step === "package"
      ? // A spa's treatments are its guests: one each, for the party the AI agreed.
        !missing.includes("package") && (kind !== "multi" || guestsComplete(a, s, tooSoon))
      : step === "guests" ? guestsComplete(a, s, tooSoon) : step === "details" ? detailsComplete(s) : missing.length === 0;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const body = chatCheckoutPayload(a, p, s, lang, tooSoon);
      const result = await createCheckoutApi(api).submit(body as never);
      const pkg = selectedPackage(a, s);
      track("booking_submitted", {
        activity_slug: a.slug,
        city: a.cityName ?? undefined,
        category: a.category?.slug ?? undefined,
        package_id: body.packageId,
        booking_id: result.bookingId,
        total: body.total,
        currency: "MAD",
        payment: paymentLine(a) === "link" ? "link" : "arrival",
        flow: "ai",
      });
      if (messageId) markProposalOpened(messageId, p);
      if (result.bookingId) {
        addLocalBooking({
          id: result.bookingId,
          reference: result.bookingId,
          slug: a.slug,
          title: a.title,
          image: undefined,
          city: a.cityName ?? "",
          date: body.date,
          time: body.time,
          guests: body.adults + body.children + body.infants,
          pkg: kind === "multi" ? packLines(a, s).map((l) => `${l.quantity} × ${l.pkg.name}`).join(", ") : (pkg?.name ?? ""),
          total: body.total,
          currency: "MAD",
          status: "pending",
          ...(paymentLine(a) === "link" ? { payByLink: true } : {}),
          ...(kind === "table" ? { tableBooking: true } : {}),
        });
      }
      // Shown at once from the checkout's own answer. The stored proof (the
      // press + the local booking) is what a reloaded chat reads later.
      onSent?.({
        reference: String(result.bookingId ?? result.id),
        date: body.date,
        ...(body.time ? { time: body.time } : {}),
        guests: body.adults + body.children + body.infants,
        at: Date.now(),
      });
    } catch (e) {
      setError(checkoutErrorText(e, t as never, minParty));
      setSending(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2.5" data-testid="chat-booking">
      <StillNeeded missing={missing} />

      {steps.map((name, i) => {
        if (i > at) return null;
        const done = i < at;
        if (done) {
          return (
            <DoneRow key={name} label={doneLabel(name)} onEdit={() => setAt(i)} />
          );
        }
        return (
          <section key={name} ref={current} className="mj-rise-in" style={{ ...panelStyle(18), padding: 12, scrollMarginTop: 72 }} data-testid={`chat-booking-step-${name}`}>
            {name === "package" && (kind === "multi" ? <Treatments a={a} s={s} set={set} money={money} /> : <Packages a={a} s={s} set={set} money={money} />)}
            {name === "guests" && <Guests a={a} p={p} s={s} set={set} money={money} tooSoon={tooSoon} />}
            {name === "details" && <Details s={s} set={set} />}
            {name === "review" && <Review a={a} p={p} s={s} shown={shown} money={money} lang={lang} />}
            {error && name === "review" ? (
              <p role="alert" className="mt-2.5 rounded-[12px] px-3 py-2 text-start text-[12.5px] font-semibold" style={{ background: "rgba(192,57,43,0.08)", color: "#B3261E" }} data-testid="chat-booking-error">
                {error}
              </p>
            ) : null}
            <div className="mt-3 flex gap-2">
              {name === "review" ? (
                <button
                  type="button"
                  disabled={!canNext || sending}
                  onClick={send}
                  className="mj-press inline-flex min-h-[42px] flex-1 items-center justify-center gap-1.5 rounded-full px-4 text-[13.5px] font-semibold disabled:opacity-45"
                  style={{ ...ON, boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}` }}
                  data-testid="chat-booking-send"
                >
                  {sending ? t("booking_sheet.sending") : t("ai_concierge.booking.send")}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={() => setAt(i + 1)}
                  className="mj-press inline-flex min-h-[42px] flex-1 items-center justify-center rounded-full px-4 text-[13.5px] font-semibold disabled:opacity-45"
                  style={{ ...ON, boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${SOFT.buttonInset}` }}
                  data-testid="chat-booking-next"
                >
                  {t("activity.continue")}
                </button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );

  function doneLabel(name: ChatBookingStep): string {
    if (name === "package") {
      return kind === "multi"
        ? packLines(a, s).map((l) => `${l.quantity} × ${l.pkg.name}`).join(", ")
        : (selectedPackage(a, s)?.name ?? "");
    }
    if (name === "guests") {
      const bits = [t("ai_concierge.proposal.adults", { count: s.adults })];
      if (kind !== "multi" && s.children > 0) bits.push(t("ai_concierge.proposal.children", { count: s.children }));
      if (kind === "multi") return t("ai_concierge.proposal.guests", { count: packLines(a, s).reduce((n, l) => n + l.quantity, 0) });
      if (pick.applied) bits.push(t("activity.hotel_pickup"));
      return bits.join(", ");
    }
    if (name === "details") return s.fullName;
    return "";
  }
}

/* ------------------------------------------------------------------ */
/* Parts                                                                */
/* ------------------------------------------------------------------ */

type Money = ReturnType<typeof useDisplayMoney>;
type SetState = (patch: Partial<ChatBookingState>) => void;

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="text-start text-[10.5px] font-bold uppercase" style={{ color: SOFT.body, letterSpacing: "0.07em" }}>
      {children}
    </p>
  );
}

function StillNeeded({ missing }: { missing: ChatBookingStep[] }) {
  const { t } = useTranslation();
  if (missing.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="chat-booking-needed">
      <span className="text-[11.5px] font-semibold" style={{ color: SOFT.body }}>
        {t("ai_concierge.booking.still_needed")}
      </span>
      {missing.map((m) => (
        <span key={m} className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: "rgba(229,178,101,0.2)", color: SOFT.goldInk }}>
          {t(`ai_concierge.booking.needed_${m}`)}
        </span>
      ))}
    </div>
  );
}

function DoneRow({ label, onEdit }: { label: string; onEdit: () => void }) {
  const { t } = useTranslation();
  return (
    // The summary wraps: it is the date, time or package the guest picked.
    <div className="flex items-center gap-2 rounded-[14px] px-3 py-2" style={CONTROL}>
      <Check size={14} strokeWidth={3} color="#1A7A44" aria-hidden="true" className="shrink-0" />
      <span className="min-w-0 flex-1 break-words text-start text-[12.5px] font-semibold" style={{ color: SOFT.ink }}>
        {label}
      </span>
      <button type="button" onClick={onEdit} className="shrink-0 text-[12px] font-bold underline" style={{ color: SOFT.ink }}>
        {t("booking_sheet.edit")}
      </button>
    </div>
  );
}

function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full"
      style={on ? { background: SOFT.inkGradient } : { background: "#fff", boxShadow: "0 0 0 1.5px rgba(17,14,57,0.14)" }}
    >
      {on ? <span className="h-[7px] w-[7px] rounded-full bg-white" /> : null}
    </span>
  );
}

function Stepper({ value, min, max, onChange, label }: { value: number; min: number; max?: number; onChange: (n: number) => void; label: string }) {
  return (
    <span className="inline-flex h-9 shrink-0 items-center rounded-full px-0.5" style={CONTROL}>
      <button type="button" aria-label={`− ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} className="grid h-8 w-8 place-items-center disabled:opacity-30" style={{ color: SOFT.ink }}>
        <Minus size={14} strokeWidth={2.6} />
      </button>
      <span className="min-w-[20px] text-center text-[13.5px] font-bold tabular-nums" style={{ color: SOFT.ink }}>
        {value}
      </span>
      <button type="button" aria-label={`+ ${label}`} disabled={max != null && value >= max} onClick={() => onChange(value + 1)} className="grid h-8 w-8 place-items-center disabled:opacity-30" style={{ color: SOFT.ink }}>
        <Plus size={14} strokeWidth={2.6} />
      </button>
    </span>
  );
}

function Price({ money, mad }: { money: Money; mad: number }) {
  const parts = money.parts(money.unit(mad));
  return (
    <span className="whitespace-nowrap text-[13px] font-bold tabular-nums" style={{ color: SOFT.ink }}>
      {parts.value} <span className="text-[9.5px]" style={{ color: SOFT.body }}>{parts.currency}</span>
    </span>
  );
}

function perks(description?: string | null): string[] {
  if (!description) return [];
  return description
    .split(/<\/(?:p|li|div)>|<br\s*\/?>|\r?\n/i)
    .map((x) => x.replace(/<[^>]+>/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .slice(0, 4);
}

/* C31 · Packages */
function Packages({ a, s, set, money }: { a: ChatActivity; s: ChatBookingState; set: SetState; money: Money }) {
  const { t } = useTranslation();
  return (
    <>
      <Eyebrow>{t("booking_sheet.step_package_title")}</Eyebrow>
      <div className="mt-2 flex flex-col gap-1.5" role="radiogroup">
        {a.packages.map((pkg) => {
          const on = s.packageId === pkg.id;
          const child = pkg.pricing.child > 0 ? money.format(money.unit(pkg.pricing.child)) : null;
          return (
            <button
              key={pkg.id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => set({ packageId: pkg.id, pickup: false })}
              className="rounded-[14px] p-2.5 text-start"
              style={{ background: on ? "#fff" : "transparent", boxShadow: on ? GOLD_EDGE : `inset 0 0 0 1px ${HAIR}` }}
              data-testid="chat-booking-package"
            >
              <span className="flex items-center gap-2.5">
                <Radio on={on} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span className="min-w-0 break-words text-[13px] font-bold" style={{ color: SOFT.ink }}>
                      {pkg.name}
                    </span>
                    {pkg.pricing.adult > 0 ? <Price money={money} mad={pkg.pricing.adult} /> : null}
                  </span>
                  {[pkg.timeStart?.slice(0, 5), child ? t("booking_sheet.child_price", { price: child }) : null].filter(Boolean).length ? (
                    <span className="block text-[11.5px] font-medium" style={{ color: SOFT.body }}>
                      {[pkg.timeStart?.slice(0, 5), child ? t("booking_sheet.child_price", { price: child }) : null].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </span>
              </span>
              {on && perks(pkg.description).length > 0 ? (
                <span className="mt-1.5 flex flex-col gap-0.5 ps-7">
                  {perks(pkg.description).map((x) => (
                    <span key={x} className="flex items-start gap-1.5 text-[11.5px] font-medium" style={{ color: "#3f4257" }}>
                      <Check size={12} color="#1A7A44" strokeWidth={3} className="mt-0.5 shrink-0" aria-hidden="true" /> {x}
                    </span>
                  ))}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </>
  );
}

/* C35 · Treatments (spas) */
function Treatments({ a, s, set, money }: { a: ChatActivity; s: ChatBookingState; set: SetState; money: Money }) {
  const { t } = useTranslation();
  const count = packLines(a, s).reduce((n, l) => n + l.quantity, 0);
  const agreed = s.women != null && s.men != null ? s.women + s.men : null;
  return (
    <>
      <Eyebrow>{t("booking_sheet.step_treatments_title")}</Eyebrow>
      <div className="mt-1">
        {a.packages.map((pkg, i) => (
          <div key={pkg.id} className="flex items-center gap-2 py-2" style={{ borderTop: i ? `1px solid ${HAIR}` : "none" }}>
            <span className="min-w-0 flex-1">
              <span className="block break-words text-start text-[13px] font-bold" style={{ color: SOFT.ink }}>
                {pkg.name}
              </span>
              {pkg.pricing.adult > 0 ? <Price money={money} mad={pkg.pricing.adult} /> : null}
            </span>
            <Stepper value={s.packs[pkg.id] ?? 0} min={0} onChange={(v) => set({ packs: { ...s.packs, [pkg.id]: v } })} label={pkg.name} />
          </div>
        ))}
      </div>
      {agreed != null && count !== agreed ? (
        <p className="mt-1 rounded-[10px] px-2.5 py-1.5 text-start text-[11.5px] font-semibold" style={{ background: "rgba(229,178,101,0.2)", color: SOFT.goldInk }} data-testid="chat-booking-mix">
          {t("ai_concierge.booking.one_each", { count: agreed })}
        </p>
      ) : null}
    </>
  );
}

/* C32 · Who is coming, and pickup */
function Guests({ a, p, s, set, money, tooSoon }: { a: ChatActivity; p: BookingProposal; s: ChatBookingState; set: SetState; money: Money; tooSoon: boolean }) {
  const { t } = useTranslation();
  const kind = chatBookingKind(a);
  const tp = typePriceOf(a, s);
  const pkg = selectedPackage(a, s);
  const pick = pickupFor(a, s, tooSoon);
  const minParty = normalizeMinPeople(a.minPeople);
  const ages = ageBracketLabels({
    infantMaxAge: (a as { infantMaxAge?: number | null }).infantMaxAge ?? null,
    childrenMaxAge: (a as { childrenMaxAge?: number | null }).childrenMaxAge ?? null,
  });
  if (kind === "multi") {
    return (
      <>
        <Eyebrow>{t("booking_sheet.step_guests_title")}</Eyebrow>
        <p className="mt-1.5 text-start text-[13px] font-semibold" style={{ color: SOFT.ink }}>
          {[
            s.women ? t("ai_concierge.proposal.women", { count: s.women }) : null,
            s.men ? t("ai_concierge.proposal.men", { count: s.men }) : null,
          ]
            .filter(Boolean)
            .join(", ") || t("ai_concierge.proposal.guests", { count: packLines(a, s).reduce((n, l) => n + l.quantity, 0) })}
        </p>
      </>
    );
  }
  const rows: { key: "adults" | "children" | "infants"; label: string; sub: string; min: number; shown: boolean }[] = [
    { key: "adults", label: t("activity.adults"), sub: t(ages.adults.key, ages.adults.params), min: 1, shown: true },
    {
      key: "children",
      label: t("activity.children"),
      sub: t(ages.children.key, ages.children.params),
      min: 0,
      shown: kind === "table" || (pkg ? pkg.pricing.child !== -1 : false),
    },
    {
      key: "infants",
      label: t("activity.infants"),
      sub: t("activity.infants_age"),
      min: 0,
      shown: kind !== "table" && (pkg ? pkg.pricing.infant !== -1 : false),
    },
  ];
  return (
    <>
      <Eyebrow>{kind === "table" ? t("booking_sheet.step_party_title") : t("booking_sheet.step_guests_title")}</Eyebrow>
      <div className="mt-1">
        {tp === 3 ? (
          <Row label={t("ai_concierge.booking.vehicles")} sub={pkg?.maxGuests ? t("ai_concierge.booking.seats_each", { count: pkg.maxGuests }) : ""}>
            <Stepper value={s.groups} min={1} onChange={(v) => set({ groups: v, adults: Math.max(s.adults, v) })} label={t("ai_concierge.booking.vehicles")} />
          </Row>
        ) : null}
        {tp === 1 ? (
          <Row label={t("activity.hours")} sub="">
            <Stepper value={s.hours} min={1} max={8} onChange={(v) => set({ hours: v })} label={t("activity.hours")} />
          </Row>
        ) : null}
        {rows
          .filter((r) => r.shown)
          .map((r) => (
            <Row key={r.key} label={r.label} sub={r.sub}>
              <Stepper value={s[r.key]} min={r.key === "adults" ? Math.max(1, tp === 3 ? s.groups : 1) : r.min} onChange={(v) => set({ [r.key]: v } as Partial<ChatBookingState>)} label={r.label} />
            </Row>
          ))}
      </div>
      {minParty > 1 && s.adults + s.children < minParty ? (
        <p className="mt-1 text-start text-[11.5px] font-semibold" style={{ color: SOFT.goldInk }}>
          {t("activity.min_people", { count: minParty, defaultValue: "Minimum {{count}} people" })}
        </p>
      ) : null}

      {pick.offered && !pick.tooSoon && !pick.withheld ? (
        pick.free ? (
          <div className="mt-2 rounded-[14px] p-2.5" style={{ background: "rgba(31,168,85,0.12)" }} data-testid="chat-booking-pickup-included">
            <p className="text-start text-[12.5px] font-bold" style={{ color: "#1A7A44" }}>
              {t("booking_sheet.pickup_included")}
            </p>
          </div>
        ) : (
          <button
            type="button"
            role="checkbox"
            aria-checked={s.pickup}
            onClick={() => set({ pickup: !s.pickup, pickupOutside: s.pickup ? false : s.pickupOutside })}
            className="mt-2 flex w-full items-center gap-2.5 rounded-[14px] p-2.5 text-start"
            style={{ background: s.pickup ? "#fff" : "transparent", boxShadow: s.pickup ? GOLD_EDGE : `inset 0 0 0 1px ${HAIR}` }}
            data-testid="chat-booking-pickup"
          >
            <Tick on={s.pickup} />
            <span className="flex-1 text-[13px] font-bold" style={{ color: SOFT.ink }}>
              {t("activity.hotel_pickup")}
            </span>
            {pick.price > 0 || pkg?.pricing.transport ? <Price money={money} mad={pick.price || pkg!.pricing.transport!} /> : null}
          </button>
        )
      ) : null}
      {pick.applied && (pkg?.pricing.transportOutside ?? 0) > 0 ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={s.pickupOutside}
          onClick={() => set({ pickupOutside: !s.pickupOutside })}
          className="mt-1.5 flex w-full items-center gap-2.5 rounded-[14px] p-2.5 text-start"
          style={{ boxShadow: `inset 0 0 0 1px ${HAIR}` }}
        >
          <Tick on={s.pickupOutside} />
          <span className="flex-1 text-[12.5px] font-semibold" style={{ color: SOFT.ink }}>
            {t("activity.pickup_outside")}
          </span>
          <Price money={money} mad={pkg!.pricing.transportOutside!} />
        </button>
      ) : null}
      {pick.applied ? (
        <Field
          label={t("activity.pickup_address")}
          value={s.pickupAddress}
          onChange={(v) => set({ pickupAddress: v })}
          placeholder={t("activity.pickup_address_placeholder")}
          valid={!!s.pickupAddress.trim()}
          testId="chat-booking-address"
        />
      ) : null}
    </>
  );
}

function Tick({ on }: { on: boolean }) {
  return (
    <span aria-hidden="true" className="grid h-5 w-5 shrink-0 place-items-center rounded-[6px]" style={on ? { background: SOFT.inkGradient } : { background: "#fff", boxShadow: "0 0 0 1.5px rgba(17,14,57,0.14)" }}>
      {on ? <Check size={13} color="#fff" strokeWidth={3.2} /> : null}
    </span>
  );
}

function Row({ label, sub, children }: { label: string; sub: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-2" style={{ borderTop: `1px solid ${HAIR}` }}>
      <span className="min-w-0 flex-1">
        <span className="block text-start text-[13px] font-bold" style={{ color: SOFT.ink }}>
          {label}
        </span>
        {sub ? (
          <span className="block text-start text-[11.5px] font-medium" style={{ color: SOFT.body }}>
            {sub}
          </span>
        ) : null}
      </span>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  valid,
  type = "text",
  testId,
  multiline,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  valid?: boolean;
  type?: string;
  testId?: string;
  multiline?: boolean;
}) {
  return (
    <label className="mt-2 block">
      <span className="mb-1 block text-start text-[10.5px] font-bold uppercase" style={{ color: SOFT.body, letterSpacing: "0.06em" }}>
        {label}
      </span>
      <span
        className="flex min-h-[42px] items-center gap-2 rounded-[12px] px-3"
        style={{ background: "#fff", boxShadow: `0 0 0 1.5px ${valid ? "rgba(26,122,68,0.4)" : "rgba(17,14,57,0.12)"}` }}
      >
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={2}
            className="min-w-0 flex-1 resize-none bg-transparent py-2 text-[15px] font-medium outline-none"
            style={{ color: SOFT.ink }}
            data-testid={testId}
          />
        ) : (
          <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="min-w-0 flex-1 bg-transparent text-[15px] font-medium outline-none"
            style={{ color: SOFT.ink }}
            data-testid={testId}
          />
        )}
        {valid ? <Check size={16} color="#1A7A44" strokeWidth={3} aria-hidden="true" /> : null}
      </span>
    </label>
  );
}

/* C33 · Your details */
function Details({ s, set }: { s: ChatBookingState; set: SetState }) {
  const { t } = useTranslation();
  return (
    <>
      <Eyebrow>{t("booking_sheet.step_details_title")}</Eyebrow>
      <p className="mt-0.5 text-start text-[12px] font-medium" style={{ color: SOFT.body }}>
        {t("booking_sheet.step_details_sub")}
      </p>
      <Field label={t("form.full_name")} value={s.fullName} onChange={(v) => set({ fullName: v })} placeholder="Sara Bennani" valid={isFullName(s.fullName)} testId="chat-booking-name" />
      <label className="mt-2 block">
        <span className="mb-1 block text-start text-[10.5px] font-bold uppercase" style={{ color: SOFT.body, letterSpacing: "0.06em" }}>
          {t("booking_sheet.phone_label")}
        </span>
        <PhoneField value={s.phone} onChange={(v) => set({ phone: v })} valid={phoneOk(s.phone)} placeholder="6 12 34 56 78" />
      </label>
      <Field label={t("activity.email")} type="email" value={s.email} onChange={(v) => set({ email: v })} placeholder="you@email.com" valid={EMAIL_SHAPE.test(s.email.trim())} testId="chat-booking-email" />
      <Field label={t("activity.notes")} value={s.notes} onChange={(v) => set({ notes: v })} placeholder={t("activity.notes_placeholder")} multiline testId="chat-booking-notes" />
    </>
  );
}

/* C40 · Review and send */
function Review({ a, p, s, shown, money, lang }: { a: ChatActivity; p: BookingProposal; s: ChatBookingState; shown: number; money: Money; lang: ChatLang }) {
  const { t } = useTranslation();
  const kind = chatBookingKind(a);
  const pay = paymentLine(a);
  const pkg = selectedPackage(a, s);
  const rows: [string, string][] = [];
  const day = new Intl.DateTimeFormat(`${lang}-u-nu-latn`, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(`${p.date.slice(0, 10)}T12:00:00Z`),
  );
  rows.push([
    t("activity.date"),
    [day, p.nights ? t("ai_concierge.proposal.nights", { count: p.nights }) : p.time].filter(Boolean).join(" · "),
  ]);
  if (kind === "multi") rows.push([t("booking_sheet.step_treatments_title"), packLines(a, s).map((l) => `${l.quantity} × ${l.pkg.name}`).join(", ")]);
  else if (pkg && kind !== "table") rows.push([t("ai_concierge.booking.package"), pkg.name]);
  return (
    <>
      <Eyebrow>{t("booking_sheet.step_review_title")}</Eyebrow>
      <div className="mt-1.5">
        {rows.map(([l, v]) => (
          <div key={l} className="flex items-baseline justify-between gap-3 py-1 text-[12.5px]">
            <span style={{ color: SOFT.body }}>{l}</span>
            <span className="text-end font-semibold" style={{ color: SOFT.ink }}>
              {v}
            </span>
          </div>
        ))}
        <div className="flex items-baseline justify-between gap-3 py-1 text-[12.5px]">
          <span style={{ color: SOFT.body }}>{t("booking_sheet.step_details_title")}</span>
          <span className="text-end font-semibold" style={{ color: SOFT.ink }}>
            {s.fullName} · <bdi dir="ltr">{s.phone}</bdi>
          </span>
        </div>
      </div>
      <div className="mt-1 flex items-baseline justify-between border-t pt-2" style={{ borderColor: HAIR }} data-testid="chat-booking-total">
        <span className="text-[10.5px] font-bold uppercase" style={{ color: SOFT.body, letterSpacing: "0.06em" }}>
          {pay === "free" ? t("ai_concierge.booking.table") : pay === "link" ? t("ai_concierge.booking.total_link") : t("booking_sheet.total_pay_arrival")}
        </span>
        <span className="text-[17px] font-extrabold tabular-nums" style={{ color: SOFT.ink }}>
          {pay === "free" ? t("ai_concierge.booking.free_to_reserve") : money.format(shown)}
        </span>
      </div>
      {pay === "link" ? (
        <p className="mt-1.5 inline-flex items-center gap-1.5 text-start text-[11.5px] font-medium" style={{ color: SOFT.body }}>
          <Lock size={11} aria-hidden="true" /> {t("ai_concierge.booking.link_note")}
        </p>
      ) : null}
    </>
  );
}
